import type { ReactNode } from 'react';
import { ArrowDown, ArrowUp, ChevronsUpDown, Inbox, TriangleAlert } from 'lucide-react';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';
import { PageLoader } from './Spinner';
import { EmptyState } from './EmptyState';

/*
  ─────────────────────────────────────────────────────────────────────────────
  One table for the whole dashboard.

  Every page used to hand-write its own <table>: its own <thead> classes, its
  own padding, its own "no results" row, and — on three of them — its own
  quietly different column label size. This owns all of that, so a change to
  how a table reads happens once.

  WHAT IT INSISTS ON

  · Numbers go right and are tabular. A column of figures is read down, and
    proportional digits make that impossible.
  · The head is sticky. These tables are read while scrolled, and a column
    label that has scrolled off turns every figure below it into a guess.
  · Empty, loading and failed are three different states with three different
    messages. "No results" for a failed request is how a reader concludes the
    gate had a quiet afternoon when in fact the API was down.
  ─────────────────────────────────────────────────────────────────────────────
*/

export interface Column<T> {
  /** Stable identity for the column; also the default sort key. */
  key: string;
  header: ReactNode;
  /** Cell contents. Given the row and its index. */
  render: (row: T, index: number) => ReactNode;
  /** `num` right-aligns and applies tabular figures. */
  align?: 'left' | 'num';
  /** Applied to the <th> — use for `w-[120px]`, `max-sm:hidden`, etc. */
  className?: string;
  sortable?: boolean;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T, index: number) => string;
  loading?: boolean;
  /** Any truthy value renders the failure state rather than the empty one. */
  error?: unknown;
  errorMessage?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;
  onRowClick?: (row: T) => void;
  isRowSelected?: (row: T) => boolean;
  /** A footer strip inside the panel — totals, or pagination. */
  footer?: ReactNode;
  sort?: { key: string; direction: 'asc' | 'desc' };
  onSortChange?: (key: string) => void;
  className?: string;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading,
  error,
  errorMessage,
  emptyTitle = 'Nothing here yet',
  emptyDescription,
  emptyAction,
  onRowClick,
  isRowSelected,
  footer,
  sort,
  onSortChange,
  className,
}: DataTableProps<T>) {
  if (loading) return <PageLoader />;

  if (error) {
    return (
      <EmptyState
        icon={<TriangleAlert size={17} />}
        title="Could not load this"
        description={
          errorMessage ??
          'The request did not come back. Check your connection and try again — this is not an empty result.'
        }
      />
    );
  }

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={<Inbox size={17} />}
        title={emptyTitle}
        description={emptyDescription}
        action={emptyAction}
      />
    );
  }

  return (
    <div className={cn('flex flex-col', className)}>
      <div className="overflow-x-auto">
        <table className={styles.table}>
          <thead className={styles.thead}>
            <tr>
              {columns.map((col) => {
                const isSorted = sort?.key === col.key;
                const sortable = col.sortable && onSortChange;
                return (
                  <th
                    key={col.key}
                    scope="col"
                    aria-sort={isSorted ? (sort!.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                    className={cn(styles.th, col.align === 'num' && 'text-right', col.className)}
                  >
                    {sortable ? (
                      <button
                        type="button"
                        onClick={() => onSortChange(col.key)}
                        className={cn(
                          'inline-flex items-center gap-1 uppercase tracking-[0.08em] transition-colors hover:text-foreground',
                          col.align === 'num' && 'flex-row-reverse',
                          isSorted && 'text-foreground',
                        )}
                      >
                        {col.header}
                        {isSorted ? (
                          sort!.direction === 'asc' ? <ArrowUp size={10} /> : <ArrowDown size={10} />
                        ) : (
                          <ChevronsUpDown size={10} className="opacity-45" />
                        )}
                      </button>
                    ) : (
                      col.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const selected = isRowSelected?.(row);
              return (
                <tr
                  key={rowKey(row, i)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cn(
                    styles.trHover,
                    onRowClick && 'cursor-pointer',
                    selected && styles.trSelected,
                    /* The last row's rule would double up with the panel's own
                       border, which reads as a 2px line under the table. */
                    'last:[&>td]:border-b-0',
                  )}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={cn(col.align === 'num' ? styles.tdNum : styles.td, col.className)}
                    >
                      {col.render(row, i)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {footer && <div className={styles.panelRow}>{footer}</div>}
    </div>
  );
}
